import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Delivery, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, generateCode, nextDocumentBranchNumber, writeAuditLog } from '@shared/utils';
import { fetchQuote, fetchQuoteItems } from '@modules/quote';
// バレル(@modules/order)ではなくサービスの直パスで import する。
// バレルは画面コンポーネントまで巻き込み、循環参照の温床になるため
// （既存の procurement.service / subcontract.service も同じ方針）。
import { advanceOrderToInProgress } from '@modules/order/services/order.service';
import { fetchCustomer } from '@modules/customer';

const lineItemSchema = z.object({
  id: z.string(),
  // 空白行（帳票レイアウト用）の保持を許容するため空文字を許可する（見積側スキーマと整合）
  title: z.string(),
  qty: z.number().min(0),
  unitId: z.string(),
  // 値引き行に対応するため負の単価・金額を許容する（見積側スキーマと整合）
  unitPrice: z.number().finite(),
  amount: z.number().finite(),
  taxRate: z.union([z.literal(10), z.literal(8), z.literal('exempt')]),
});

const stampFlagsSchema = z.object({
  tantou: z.boolean().default(false),
  kakunin: z.boolean().default(false),
  kaisha: z.boolean().default(false),
});

const deliveryCreateSchema = z.object({
  orderId: z.string().min(1),
  quoteId: z.string().nullable().default(null),
  customerId: z.string().min(1),
  title: z.string().default(''),
  issueDate: z.instanceof(Timestamp),
  deliveryDate: z.instanceof(Timestamp).nullable().default(null),
  deliveredAt: z.instanceof(Timestamp).nullable().default(null),
  chiefEmployerId: z.string().nullable().default(null),
  usergroupId: z.string().nullable().default(null),
  stampFlags: stampFlagsSchema.default({ tantou: false, kakunin: false, kaisha: false }),
  lineItems: z.array(lineItemSchema).default([]),
  subtotal: z.number().min(0).default(0),
  advance: z.number().min(0).finite().default(0),
  totalgst: z.number().min(0).finite().default(0),
  grandtotal: z.number().min(0).finite().default(0),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  printNote: z.string().nullable().default(null),
  internalNote: z.string().nullable().default(null),
  sourceDocId: z.string().nullable().default(null),
  isSplit: z.boolean().default(false),
  splitSeq: z.number().int().min(1).default(1),
  assignedUserId: z.string().min(1),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

export type DeliveryCreateInput = z.infer<typeof deliveryCreateSchema>;
export type DeliveryUpdateInput = Partial<Omit<DeliveryCreateInput, 'orderId' | 'customerId'>>;

const COL = 'deliveries';

export async function fetchDeliveries(filters?: {
  orderId?: ID;
  customerId?: ID;
}): Promise<Delivery[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('issueDate', 'desc'),
  );
  if (filters?.orderId) q = query(q, where('orderId', '==', filters.orderId));
  if (filters?.customerId) q = query(q, where('customerId', '==', filters.customerId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Delivery);
}

export async function fetchDelivery(id: ID): Promise<Delivery | null> {
  return fromSnapshot<Delivery>(await getDoc(doc(db, COL, id)));
}

function buildPartnerSnapshot(customer: Awaited<ReturnType<typeof fetchCustomer>>) {
  if (!customer) return null;
  return {
    name: customer.name,
    furi: customer.nameKana,
    masterCode: customer.code,
    zip: customer.postalCode,
    prefecture: customer.prefecture,
    addr1: customer.addr1,
    addr2: customer.addr2,
    addr3: customer.addr3,
    organize: customer.department ?? '',
    tel: customer.phone,
    ...(customer.tel2 ? { tel2: customer.tel2 } : {}),
  };
}

/**
 * 重複判定用に明細行を正規化する。
 * id・amount（派生値）は比較対象から除外し、品名・数量・単位・単価・税率のみで比較する。
 */
function normalizeLineItemsForDuplicateCheck(
  lineItems: DeliveryCreateInput['lineItems'],
): Array<{ title: string; qty: number; unitId: string; unitPrice: number; taxRate: 10 | 8 | 'exempt' }> {
  return lineItems.map((it) => ({
    title: it.title,
    qty: it.qty,
    unitId: it.unitId,
    unitPrice: it.unitPrice,
    taxRate: it.taxRate,
  }));
}

/**
 * 同一案件・同一顧客で、明細と合計金額が完全に一致する未削除の納品書が既に存在するか検証する。
 * 一致する場合は重複として保存を拒否する（発行日などの日時は比較対象外）。
 */
async function assertNoDuplicateDelivery(input: DeliveryCreateInput): Promise<void> {
  if (!input.orderId) return;
  const snap = await getDocs(query(collection(db, COL), where('orderId', '==', input.orderId)));
  const targetItems = JSON.stringify(normalizeLineItemsForDuplicateCheck(input.lineItems));
  const isDuplicate = snap.docs.some((d) => {
    const data = d.data() as Record<string, unknown>;
    // 論理削除済みは重複対象外
    if (data['isDeleted'] === true) return false;
    if ((data['customerId'] as string | undefined) !== input.customerId) return false;
    if (((data['grandtotal'] as number | undefined) ?? 0) !== input.grandtotal) return false;
    const items = (data['lineItems'] as DeliveryCreateInput['lineItems'] | undefined) ?? [];
    return JSON.stringify(normalizeLineItemsForDuplicateCheck(items)) === targetItems;
  });
  if (isDuplicate) {
    throw new Error('同一内容の納品書が既に存在するため、保存できません。');
  }
}

export async function createDelivery(data: DeliveryCreateInput, userId: ID): Promise<Delivery> {
  // parseOrThrow は zod の Input 型（デフォルト適用前）と Output 型の両立を要求するため、
  // デフォルト適用済みの Output 型として明示する（実行時の値は zod が既に検証・補完済み）。
  const validated = parseOrThrow(deliveryCreateSchema, data) as DeliveryCreateInput;
  await assertNoDuplicateDelivery(validated);
  const customer = await fetchCustomer(validated.customerId);
  const partnerSnapshot = buildPartnerSnapshot(customer);
  // 案件帳票は「受注番号-枝番」(0001-01) を code に持つ。案件に紐づかない場合のみ PREFIX-00001。
  const branch = await nextDocumentBranchNumber('deliveries', validated.orderId ?? null);
  const code = branch ?? (await generateCode('DLV'));
  const payload = {
    ...validated,
    code,
    partnerSnapshot,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({
    entityType: 'delivery',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });

  // 納品書を作った＝案件は動き出している。手動での更新忘れを防ぐため自動で進める。
  // 進めるのは「見積り」段階の案件だけ（完了・失注は巻き戻さない。ガードは order 側）。
  // 案件の更新に失敗しても納品書の作成は成功させる（帳票を人質にしない。
  // 既存の syncOrder*Total も同じ方針）。
  await advanceOrderToInProgress(validated.orderId, userId).catch(() => undefined);

  return { id: ref.id, ...payload } as unknown as Delivery;
}

export async function createDeliveryFromQuote(
  quoteId: ID,
  orderId: ID,
  userId: ID,
  /** 継承値への上書き（例: AI経路の deliveryDate / aiPendingReview）。1回の作成で原子的に反映する。 */
  overrides?: Partial<DeliveryCreateInput>,
): Promise<Delivery> {
  const quote = await fetchQuote(quoteId);
  if (!quote) throw new Error('見積が見つかりません。');
  const items = await fetchQuoteItems(quoteId);
  const lineItems = items.map((item) => ({
    id: item.id,
    title: item.name,
    qty: item.quantity,
    unitId: item.unitId ?? '',
    unitPrice: item.unitPrice,
    amount: item.subtotal,
    taxRate: (
      item.taxType === 'standard' ? 10 : item.taxType === 'reduced' ? 8 : 'exempt'
    ) as 10 | 8 | 'exempt',
  }));
  const input: DeliveryCreateInput = {
    orderId,
    quoteId,
    customerId: quote.customerId,
    // Mr.販管仕様: 見積から件名・確認者・捺印・担当部署を継承する。納品日は空欄（手動入力）。
    title: quote.title ?? '',
    issueDate: Timestamp.now(),
    deliveryDate: null,
    deliveredAt: null,
    chiefEmployerId: quote.chiefEmployerId ?? null,
    usergroupId: quote.usergroupId ?? null,
    stampFlags: quote.stampFlags ?? { tantou: false, kakunin: false, kaisha: false },
    lineItems,
    subtotal: quote.subtotal,
    advance: quote.advance ?? 0,
    totalgst: quote.totalgst ?? 0,
    grandtotal: quote.grandtotal ?? quote.total,
    taxCalcMode: quote.taxCalcMode ?? 'floor',
    printNote: quote.notes,
    internalNote: quote.internalNotes,
    sourceDocId: quoteId,
    isSplit: false,
    splitSeq: 1,
    assignedUserId: quote.assignedUserId,
  };
  return createDelivery({ ...input, ...overrides }, userId);
}

export async function updateDelivery(
  id: ID,
  data: DeliveryUpdateInput,
  userId: ID,
): Promise<Delivery> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('納品書が見つかりません。');
  const before = snap.data();
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'delivery',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: data,
  });
  return { id, ...before, ...patch } as unknown as Delivery;
}

export async function deleteDelivery(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('納品書が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'delivery',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
